import { Button, SegmentedControl, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconBath,
  IconBed,
  IconBuildingCommunity,
  IconHome,
  IconPlus,
  IconTable,
  IconUsers,
} from '@tabler/icons-react';
import { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { useDeleteBuilding } from 'src/Hooks/Mutations/useCartographyMutations';
import useFetchCartographySummary from 'src/Hooks/Queries/useFetchCartographySummary';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { trackStructureViewToggled, trackSummaryDetailOpened } from 'src/Services/Mixpanel/events';
import { BedType, BuildingDto, RoomFeature, RoomType } from 'src/types/cartography.types';
import { countBedrooms, countOtherSleepingRooms } from 'src/Utils/bedroomCounts';

import { BuildingCard } from '../BuildingCard';
import { BuildingDrawer } from '../BuildingDrawer';
import { BuildingHouse } from '../BuildingHouse';
import { MetricCard } from '../MetricCard';
import { OpenRoomOptions } from '../RoomCard';
import { RoomDetailDrawer } from '../RoomDetailDrawer';
import { SummaryDetailModal, SummaryMetric } from '../SummaryDetailModal';

import styles from './CartographyOverview.module.scss';
import { Props } from './CartographyOverview.types';

type CartographyView = 'house' | 'table';

// « Entrée, Cuisine, Salon, 5 chambres et 4 autres. 15 personnes. »
const MAX_NAMED_TYPES = 6;

// Keep acronyms like « WC / Toilettes » readable mid-sentence : lowering their
// first letter would turn them into « wc / toilettes ».
const lowerFirst = (value: string): string => {
  const isUpperLetter = (c: string) => c !== c.toLowerCase() && c === c.toUpperCase();
  if (value.length >= 2 && isUpperLetter(value[0]) && isUpperLetter(value[1])) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
};

// Preference persists across sessions per bien so a user who curates the main house in
// table view keeps that view when they come back tomorrow.
const viewStorageKey = (assetId: string | null | undefined) =>
  assetId ? `cartography.view.${assetId}` : null;

const readInitialView = (assetId: string | null | undefined): CartographyView => {
  const key = viewStorageKey(assetId);
  if (!key) return 'house';
  const stored = localStorage.getItem(key);
  return stored === 'table' ? 'table' : 'house';
};

type BedEntry = { key: string; count: number };

/** Carte « Lits » : tous les types, pluriel, triés par nombre descendant. */
const formatBedTypologyFull = (byType: BedEntry[], t: TFunction): string =>
  [...byType]
    .sort((a, b) => b.count - a.count)
    .map(entry =>
      t(`admin.structure.beds.typologyFull.${entry.key}`, {
        count: entry.count,
        defaultValue: `${entry.count} ${entry.key}`,
      })
    )
    .join(' · ');

/**
 * Carte « Capacité d'accueil » : les doubles agrégés (DOUBLE+QUEEN+KING) et les
 * simples (SINGLE) viennent en premier ; les autres types restent nommés
 * individuellement s'ils existent. Le total ouvre la phrase (« 7 lits : … »).
 */
const formatBedTypologyGrouped = (totalBeds: number, byType: BedEntry[], t: TFunction): string => {
  const counts = new Map<string, number>();
  for (const entry of byType) counts.set(entry.key, (counts.get(entry.key) ?? 0) + entry.count);

  const doubleGroup =
    (counts.get(BedType.DOUBLE) ?? 0) +
    (counts.get(BedType.QUEEN) ?? 0) +
    (counts.get(BedType.KING) ?? 0);
  const singleGroup = counts.get(BedType.SINGLE) ?? 0;
  const otherTypes = [BedType.BUNK, BedType.SOFA_BED, BedType.BABY, BedType.COT] as const;

  const parts: string[] = [];
  if (doubleGroup > 0) {
    parts.push(
      t('admin.structure.beds.typologyGrouped.DOUBLE', {
        count: doubleGroup,
        defaultValue: `${doubleGroup} double`,
        defaultValue_other: `${doubleGroup} doubles`,
      })
    );
  }
  if (singleGroup > 0) {
    parts.push(
      t('admin.structure.beds.typologyGrouped.SINGLE', {
        count: singleGroup,
        defaultValue: `${singleGroup} simple`,
        defaultValue_other: `${singleGroup} simples`,
      })
    );
  }
  for (const type of otherTypes) {
    const count = counts.get(type) ?? 0;
    if (count === 0) continue;
    parts.push(
      t(`admin.structure.beds.typologyFull.${type}`, {
        count,
        defaultValue: `${count} ${type}`,
      })
    );
  }

  const total = t('admin.structure.metrics.capacityBedsTotal', {
    count: totalBeds,
    defaultValue: '{{count}} lit',
    defaultValue_other: '{{count}} lits',
  });
  return parts.length > 0 ? `${total} : ${parts.join(' · ')}` : total;
};

const CartographyOverview = ({ tree }: Props) => {
  const { t } = useTranslation();
  const { data: summary } = useFetchCartographySummary();
  const assetId = useCurrentAssetId();

  const [buildingDrawerOpen, setBuildingDrawerOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<BuildingDto | undefined>();

  // The open room lives in the URL (`?room=`) so the "Lié à" panel of another fiche can
  // link straight to it, and the "Vous venez de" trail can come back to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRoomId = searchParams.get('room');
  const [openBedFormOnMount, setOpenBedFormOnMount] = useState(false);
  const [detailMetric, setDetailMetric] = useState<SummaryMetric | null>(null);

  const setSelectedRoomId = (roomId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (roomId) next.set('room', roomId);
    else next.delete('room');
    setSearchParams(next);
  };

  const handleOpenRoom = (roomId: string, options?: OpenRoomOptions) => {
    setSelectedRoomId(roomId);
    setOpenBedFormOnMount(!!options?.openBedForm);
  };

  const handleCloseRoomDetail = () => {
    setSelectedRoomId(null);
    setOpenBedFormOnMount(false);
  };

  const [view, setViewState] = useState<CartographyView>(() => readInitialView(assetId));

  const setView = (next: CartographyView) => {
    setViewState(next);
    const key = viewStorageKey(assetId);
    if (key) localStorage.setItem(key, next);
    trackStructureViewToggled({ view: next });
  };

  const openDetail = (metric: SummaryMetric) => {
    trackSummaryDetailOpened({ metric });
    setDetailMetric(metric);
  };

  const { mutate: deleteBuilding } = useDeleteBuilding();

  // La pièce sélectionnée se met à jour automatiquement après chaque mutation.
  const selectedRoom = useMemo(() => {
    if (!selectedRoomId) return undefined;
    for (const building of tree.buildings) {
      const room = building.rooms.find(r => r.id === selectedRoomId);
      if (room) return room;
    }
    return undefined;
  }, [selectedRoomId, tree.buildings]);

  const describeBuilding = (building: BuildingDto): string => {
    const activeRooms = building.rooms.filter(room => !room.isOutOfService);
    if (activeRooms.length === 0) {
      return t('admin.structure.buildings.summaryEmpty', 'Aucune pièce renseignée.');
    }

    const countByType = new Map<RoomType, number>();
    for (const room of activeRooms) {
      if (room.type === RoomType.OTHER) continue;
      countByType.set(room.type, (countByType.get(room.type) ?? 0) + 1);
    }
    const otherCount = activeRooms.filter(room => room.type === RoomType.OTHER).length;

    const sorted = Array.from(countByType.entries()).sort((a, b) => b[1] - a[1]);
    const named = sorted.slice(0, MAX_NAMED_TYPES);
    const remaining =
      sorted.slice(MAX_NAMED_TYPES).reduce((sum, [, count]) => sum + count, 0) + otherCount;

    const parts = named.map(([type, count]) => {
      const label = lowerFirst(
        t(`admin.structure.roomTypes.${type}`, {
          count,
          defaultValue: type,
        })
      );
      return count > 1 ? `${count} ${label}` : label;
    });

    if (remaining > 0) {
      parts.push(
        t('admin.structure.buildings.summaryOthers', {
          count: remaining,
          defaultValue: '{{count}} autre',
          defaultValue_other: '{{count}} autres',
        })
      );
    }

    const capacity = t('admin.structure.buildings.summaryCapacity', {
      count: building.capacity,
      defaultValue: '{{count}} personne',
      defaultValue_other: '{{count}} personnes',
    });

    return `${parts.join(', ')}. ${capacity}.`;
  };

  const handleEdit = (building: BuildingDto) => {
    setEditingBuilding(building);
    setBuildingDrawerOpen(true);
  };

  const handleDelete = (building: BuildingDto) => {
    modals.openConfirmModal({
      title: t('admin.structure.buildings.deleteTitle', 'Supprimer ce bâtiment ?'),
      children: (
        <Text size="sm">
          {t('admin.structure.buildings.deleteConfirmDetailed', {
            name: building.name,
            rooms: building.rooms.length,
            capacity: building.capacity,
            defaultValue:
              '« {{name}} », ses {{rooms}} pièces et leurs couchages seront supprimés : la capacité d’accueil baisse de {{capacity}}.',
          })}
        </Text>
      ),
      labels: { confirm: t('common.delete', 'Supprimer'), cancel: t('common.cancel', 'Annuler') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteBuilding(building.id),
    });
  };

  const handleCloseBuildingDrawer = () => {
    setBuildingDrawerOpen(false);
    setEditingBuilding(undefined);
  };

  const handleOpenCreate = () => {
    setEditingBuilding(undefined);
    setBuildingDrawerOpen(true);
  };

  // Chambres metric = BEDROOM + MASTER_BEDROOM only. Mezzanine / dortoir / studio
  // appear in the sub-caption below, never in the big number — a client (Henri, 09/26)
  // read "11" as a wrong count because the detailed table only showed 9 doors labelled
  // "Chambre". Same helper as the Dashboard card, so both screens show one figure.
  const bedroomPlusMasterCount = countBedrooms(summary);
  const otherSleepingTypes = countOtherSleepingRooms(summary);

  const bedroomsOtherSub =
    summary && otherSleepingTypes.length > 0
      ? t('admin.structure.metrics.bedroomsOther', {
          list: otherSleepingTypes
            .map(({ type, count }) => {
              const label = lowerFirst(
                t(`admin.structure.roomTypes.${type}`, {
                  count,
                  defaultValue: type,
                })
              );
              return `${count} ${label}`;
            })
            .join(', '),
          defaultValue: '+ {{list}}',
        })
      : undefined;

  // Fixtures are counted across every room, so a bathroom with a WC does not become two rooms.
  const featureCount = (feature: RoomFeature): number =>
    summary?.washrooms.byFeature.find(entry => entry.key === feature)?.count ?? 0;

  const washroomsSub = [
    ...([RoomFeature.BATHTUB, RoomFeature.SHOWER] as const)
      .map(feature => ({ feature, count: featureCount(feature) }))
      .filter(({ count }) => count > 0)
      .map(
        ({ feature, count }) =>
          `${count} ${t(`admin.structure.roomFeatures.${feature}`, feature).toLowerCase()}`
      ),
    ...(summary && summary.washrooms.enSuite > 0
      ? [
          t('admin.structure.metrics.bathroomsEnSuite', {
            count: summary.washrooms.enSuite,
            defaultValue: '{{count}} attenante(s) à une chambre',
          }),
        ]
      : []),
  ].join(' · ');

  return (
    <Stack gap="lg" className={styles.container}>
      <PageHeader
        label={t('admin.structure.title', 'Structure du bien')}
        rightElement={
          <Button variant="primary" leftSection={<IconPlus size={18} />} onClick={handleOpenCreate}>
            {t('admin.structure.buildings.add', 'Ajouter un bâtiment')}
          </Button>
        }
      />

      <Text c="dimmed" className={styles.subtitle}>
        {t(
          'admin.structure.subtitle',
          'Décrivez les bâtiments, niveaux, pièces et couchages de votre bien.'
        )}
      </Text>

      <div className={styles.metricsGrid}>
        <MetricCard
          icon={IconBed}
          label={t('admin.structure.metrics.bedrooms', 'Chambres')}
          value={bedroomPlusMasterCount}
          sub={bedroomsOtherSub}
          onClick={() => openDetail('bedrooms')}
        />
        <MetricCard
          icon={IconBed}
          label={t('admin.structure.metrics.beds', 'Lits')}
          value={summary?.bedCount ?? 0}
          sub={
            summary && summary.beds.byType.length > 0
              ? formatBedTypologyFull(summary.beds.byType, t)
              : undefined
          }
          onClick={() => openDetail('beds')}
        />
        <MetricCard
          icon={IconBath}
          label={t('admin.structure.metrics.bathrooms', "Salles de bain et salles d'eau")}
          value={summary?.bathroomCount ?? 0}
          sub={summary ? washroomsSub : undefined}
          onClick={() => openDetail('bathrooms')}
        />
        <MetricCard
          icon={IconUsers}
          label={t('admin.structure.metrics.capacity', "Capacité d'accueil")}
          value={summary?.totalCapacity ?? 0}
          unit={t('admin.structure.metrics.capacityUnit', {
            count: summary?.totalCapacity ?? 0,
            defaultValue: 'personne',
            defaultValue_other: 'personnes',
          })}
          variant="hero"
          sub={
            summary ? formatBedTypologyGrouped(summary.bedCount, summary.beds.byType, t) : undefined
          }
          onClick={() => openDetail('capacity')}
        />
      </div>

      <div className={styles.buildingsSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            {t('admin.structure.buildings.title', 'Bâtiments')}
          </h3>
          <SegmentedControl
            value={view}
            onChange={value => setView(value as CartographyView)}
            data={[
              {
                value: 'house',
                label: (
                  <span className={styles.segLabel}>
                    <IconHome size={14} />
                    {t('cartography.views.house', 'Vue maison')}
                  </span>
                ),
              },
              {
                value: 'table',
                label: (
                  <span className={styles.segLabel}>
                    <IconTable size={14} />
                    {t('cartography.views.table', 'Vue tableau')}
                  </span>
                ),
              },
            ]}
          />
        </div>

        {tree.buildings.length === 0 ? (
          <div className={styles.empty}>
            <IconBuildingCommunity size={40} className={styles.emptyIcon} />
            <span className={styles.emptyTitle}>
              {t('admin.structure.buildings.empty', 'Aucun bâtiment renseigné')}
            </span>
            <span className={styles.emptyHint}>
              {t(
                'admin.structure.buildings.emptyHint',
                'Commencez par ajouter le bâtiment principal de votre bien.'
              )}
            </span>
          </div>
        ) : view === 'house' ? (
          <div className={styles.houseGrid}>
            {tree.buildings.map(building => (
              <BuildingHouse
                key={building.id}
                building={building}
                summary={describeBuilding(building)}
                onOpenRoom={handleOpenRoom}
                onEditBuilding={handleEdit}
                onDeleteBuilding={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className={styles.tableList}>
            {tree.buildings.map(building => (
              <BuildingCard
                key={building.id}
                building={building}
                summary={describeBuilding(building)}
                onOpenRoom={handleOpenRoom}
                onEditBuilding={handleEdit}
                onDeleteBuilding={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      <BuildingDrawer
        opened={buildingDrawerOpen}
        building={editingBuilding}
        onClose={handleCloseBuildingDrawer}
      />

      <RoomDetailDrawer
        opened={!!selectedRoomId}
        room={selectedRoom}
        openBedFormOnMount={openBedFormOnMount}
        onClose={handleCloseRoomDetail}
      />

      <SummaryDetailModal
        metric={detailMetric}
        summary={summary}
        onClose={() => setDetailMetric(null)}
      />
    </Stack>
  );
};

export default CartographyOverview;
